import React, { useEffect, useState } from "react"
import { ZoomIn, X, Boxes, ArrowRight } from "lucide-react"
import { ServiceItem } from "../types/objects"
import { SectionHeading } from "./ui/SectionHeading"

/**
 * Props interface for Services component
 */
interface ServicesProps {
	data?: ServiceItem[]
}

/**
 * Services Component — core services and the AI vs. human implementation story.
 * Points to the product ecosystem and the inquiry wizard that follow this section.
 */
export const Services: React.FC<ServicesProps> = ({ data }) => {
	// --- Image lightbox ---
	const [zoomedImage, setZoomedImage] = useState<string | null>(null)

	/** Close the lightbox with Escape */
	useEffect(() => {
		if (!zoomedImage) return
		const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setZoomedImage(null) }
		window.addEventListener("keydown", onKey)
		return () => window.removeEventListener("keydown", onKey)
	}, [zoomedImage])

	return (
		<section id="services" className="lp-section lp-section--dark">
			<div className="lp-container">

				{/* --- Section Title & Business Hook --- */}
				<SectionHeading
					eyebrow="Služby"
					title="Služby & Naše Vize"
					lead={
						<>
							S využitím <strong>umělé inteligence</strong> vám pomůžeme bleskově nastartovat základy vašeho byznysu.
							Jakmile je infrastruktura připravena, přebírá iniciativu naše <strong>lidská expertíza</strong>.
							Zajistíme pro vás komplexní správu, složité integrace a plynulý chod – například prostřednictvím vašich
							vlastních, na míru vyvinutých aplikací s prvky AI.
						</>
					}
				/>

				{/* --- Service Display Grid --- */}
				<div className="lp-grid lp-grid--3">
					{data && data.map((item, i) => (
						<article
							key={i}
							className="lp-card lp-card--glass lp-service"
							data-reveal=""
							style={{ "--reveal-delay": `${(i % 3) * 100}ms` } as React.CSSProperties}
						>
							<span className="lp-service__icon" aria-hidden="true">
								<i className={item.icon} />
							</span>
							<h3 className="lp-card__title">{item.title}</h3>
							<p className="lp-card__text">{item.text}</p>
						</article>
					))}
				</div>

				{/* --- AI vs Human Implementation (The 80/20 Trap) --- */}
				<div className="lp-split" data-reveal="">
					<div className="lp-split__full">
						<h3>Jak implementujeme změny: Past 80/20 v AI programování</h3>
						<p>
							Umělá inteligence je výborná pro bleskový start. V první fázi řeší nudnou rutinu, zatímco my fungujeme jako dohled.
							Jakmile ale projekt roste do šířky, naráží na realitu: specifické firemní procesy, bezpečnostní politiky, komunikaci se starými systémy a účetnictvím třetích stran.
							Zde AI ztrácí kontext a dominanci přebírá zkušený lidský programátor. O složitou architekturu a údržbu se postará člověk, jinak se systém rozpadne.
						</p>
					</div>

					<div>
						<button
							type="button"
							className="lp-zoom"
							onClick={() => setZoomedImage("/img/ai_vs_human_devel_schema.jpg")}
							aria-label="Zvětšit schéma: Využití AI vs. Člověka ve vývoji"
						>
							<img src="/img/ai_vs_human_devel_schema.jpg" alt="Využití AI vs. Člověka ve vývoji" loading="lazy" />
							<span className="lp-zoom__hint"><ZoomIn size={14} /> Zvětšit</span>
						</button>
					</div>

					<div>
						<h4>Vlastní B2B Nástroje na míru</h4>
						<p style={{ marginBottom: 20 }}>
							Nenecháme vás bojovat se složitými integracemi samotné. Navrhujeme a programujeme specializované
							desktopové i webové aplikace, jako je náš <strong>B2B Business Optimizer</strong>. Zatímco AI nám
							pomohla rychle sestavit uživatelské rozhraní a matematický model pro lineární programování, naši inženýři
							nástroj bezpečně zakomponovali do celkové architektury tak, aby spolehlivě optimalizoval vaši výrobu i prodej.
						</p>
						<button
							type="button"
							className="lp-zoom"
							onClick={() => setZoomedImage("/img/B2BBusinessOptimizer.jpg")}
							aria-label="Zvětšit náhled aplikace B2B Business Optimizer"
						>
							<img src="/img/B2BBusinessOptimizer.jpg" alt="B2B Business Optimizer App" loading="lazy" />
							<span className="lp-zoom__hint"><ZoomIn size={14} /> Zvětšit</span>
						</button>
					</div>
				</div>

				{/* --- Pointer to the product ecosystem and the inquiry wizard --- */}
				<div className="lp-actions-center" data-reveal="">
					<a href="#products" className="lp-btn lp-btn--primary">
						<Boxes size={18} /> Prohlédnout naše produkty
					</a>
					<a href="#inquiry" className="lp-btn lp-btn--ghost">
						Sestavit poptávku <ArrowRight size={16} />
					</a>
				</div>
			</div>

			{/* --- Image Lightbox Modal --- */}
			{zoomedImage && (
				<div className="lp-lightbox" onClick={() => setZoomedImage(null)} role="dialog" aria-modal="true" aria-label="Zvětšený náhled">
					<button type="button" className="lp-lightbox__close" aria-label="Zavřít" onClick={() => setZoomedImage(null)}>
						<X size={20} />
					</button>
					<img src={zoomedImage} alt="Zvětšený náhled" />
				</div>
			)}
		</section>
	)
}
